import { zodResolver } from '@hookform/resolvers/zod'
import { toFullName } from '@repo/toolkit/to-full-name'
import { BellIcon } from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import z, { ZodType } from 'zod'

import { sendLockedTripBookingUpdateNotification } from '../../../api/trip-bookings/update-trip-booking'
import { getUsers } from '../../../api/user/get-users'
import { RoleEnum } from '../../../lib/types/enums/users/role.enum'
import {
  getSubmitAction,
  modalButtonConfig
} from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { createStringArraySchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledMultiSelectInput from '../../elements/inputs/controlled-multi-select-input'
import ValidationButtons from '../../elements/validation-buttons'

type NotificationFormInput = {
  emails: string[]
}

type Props = {
  tripBookingId: number
  label: string
  isOpen: boolean
  closeModal: () => void
  onSuccess?: () => void
}

const formSchema: ZodType<NotificationFormInput> = z.object({
  emails: createStringArraySchema('emails')
})

export function TripBookingLockedNotificationModal({
  tripBookingId,
  label,
  isOpen,
  closeModal,
  onSuccess
}: Props) {
  const form = useForm<NotificationFormInput>({
    resolver: zodResolver(formSchema),
    defaultValues: {},
    mode: FORM_MODE
  })

  const { mutateAsync: sendNotificationMutation } = useMutation({
    mutationFn: sendLockedTripBookingUpdateNotification,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Les emails ont été envoyés avec succès')
      closeModal()
      if (onSuccess) onSuccess()
    }
  })

  const validationButtonProps = modalButtonConfig({
    confirmText: 'Envoyer',
    onCancel: () => closeModal(),
    onConfirm: (values: NotificationFormInput) => {
      // we are not waiting for the response to close the modal
      // since we are sending the email to the admins and we don't
      // want to block the user from doing other actions
      sendNotificationMutation({ id: tripBookingId, emails: values.emails })
      form.reset()
      closeModal()
    }
  })

  const { data: users } = useQuery({
    queryKey: ['users', 'admins', 'master-lec'],
    queryFn: () => getUsers({ filter: { roles: [RoleEnum.ADMIN] } }),
    enabled: isOpen
  })

  const usersOptions = users?.data?.map((u) => ({
    label: toFullName(u.firstName, u.lastName),
    value: u.email
  }))

  return (
    <Modal
      hideActions
      size="medium"
      icon={BellIcon}
      title={'Envoyer une notification'}
      isOpen={isOpen}
      onCancel={closeModal}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps))}>
          <div className={'flex gap-4 flex-col modal-body'}>
            <Typography variant="tag">{label}</Typography>

            <FormField
              control={form.control}
              name={'emails'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledMultiSelectInput
                      required
                      variant="primary"
                      dropDownPosition="above"
                      searchable
                      {...field}
                      type={'text'}
                      label={'Prévenir des collaborateurs LEC'}
                      options={usersOptions}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <ValidationButtons buttonsConfig={validationButtonProps} hasBorderTop />
        </form>
      </Form>
    </Modal>
  )
}
